import { createContext, createElement, useContext, useMemo, type ReactNode } from "react";

export interface AuthUser {
  userId: string;
  displayName: string;
  email: string;
  tenant: string;
  roles: string[];
}

export interface AuthContextValue {
  // 6.5 de-mock: `null` = no resolved session (signed-out). There is NO default user — the old
  // hardcoded DEFAULT_USER (admin+analyst) was the mock this batch removed. Consumers MUST treat
  // null as unauthenticated and `hasRole` returns false for null (fail-closed).
  user: AuthUser | null;
  signOut: () => void;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// The provider is PROP-DRIVEN (single source, R-M2): the host (AppShell) resolves the real session
// identity from the backend `/auth/session` (fail-closed) and passes it in. This package holds no
// identity source of its own and NO default — a null `user` renders as signed-out, never admin.
// `signOut` is a no-op here: real sign-out is the host's session logout (src/lib/auth); this bridge
// only reflects the resolved identity.
export function AuthProvider({
  children,
  user,
}: {
  children: ReactNode;
  user: AuthUser | null;
}) {
  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      signOut: () => undefined,
      hasRole: (role) => user?.roles.includes(role) ?? false,
    }),
    [user],
  );

  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
